import React, { useEffect, useState } from "react";
import ChipSearchInput from "@/components/CustomInput/ChipInputField";
import type { MarketData } from "@/types/companyProfile";
import type { TenantProfilePayload } from "@/types/tenantService";

interface Props {
  formData: TenantProfilePayload;
  setFormData: React.Dispatch<React.SetStateAction<TenantProfilePayload>>;
  data?: MarketData;
  errors?: Record<string, string>;
  setFormErrors?: React.Dispatch<React.SetStateAction<Record<string, string>>>;
}

const MarketLandscape: React.FC<Props> = ({
  formData,
  setFormData,
  data,
  errors = {},
  setFormErrors,
}) => {
  const [competitorOptions, setCompetitorOptions] = useState<string[]>([]);
  const [uspOptions, setUspOptions] = useState<string[]>([]);
  const [industryOptions, setIndustryOptions] = useState<string[]>([]);

  useEffect(() => {
    if (data) {
      setFormData((prev) => ({
        ...prev,
        competitors: data.competitors ?? [],
        uniqueSellingPoints: data.uniqueSellingPoints ?? [],
        targetIndustries: data.targetIndustries ?? [],
      }));

      setCompetitorOptions(data.competitors ?? []);
      setUspOptions(data.uniqueSellingPoints ?? []);
      setIndustryOptions(data.targetIndustries ?? []);
    }
  }, [data, setFormData]);

  const handleChange = (
    key: keyof TenantProfilePayload,
    newValues: string[],
    setOptions: React.Dispatch<React.SetStateAction<string[]>>,
  ) => {
    setFormData((prev) => ({ ...prev, [key]: newValues }));
    setOptions((prev) => Array.from(new Set([...prev, ...newValues])));

    // clear error if field has valid value
    setFormErrors?.((prev) => {
      const updated = { ...prev };
      if (newValues.length > 0) delete updated[key];
      return updated;
    });
  };

  const handleBlur = (key: keyof TenantProfilePayload, values: string[]) => {
    if (setFormErrors) {
      setFormErrors((prev) => {
        const updated = { ...prev };
        if (values.length === 0) {
          updated[key] = "This field is required.";
        } else {
          delete updated[key];
        }
        return updated;
      });
    }
  };

  return (
    <div className="text-white space-y-4">
      <ChipSearchInput
        label="Competitors"
        values={formData.competitors}
        options={competitorOptions}
        onChange={(newValues) =>
          handleChange("competitors", newValues, setCompetitorOptions)
        }
        onBlur={() => handleBlur("competitors", formData.competitors)}
        error={errors.competitors} // ✅ always show parent error if exists
      />

      <ChipSearchInput
        label="Unique Selling Points"
        values={formData.uniqueSellingPoints}
        options={uspOptions}
        onChange={(newValues) =>
          handleChange("uniqueSellingPoints", newValues, setUspOptions)
        }
        onBlur={() =>
          handleBlur("uniqueSellingPoints", formData.uniqueSellingPoints)
        }
        error={errors.uniqueSellingPoints} // ✅ same here
      />

      <ChipSearchInput
        label="Target Industries"
        values={formData.targetIndustries}
        options={industryOptions}
        onChange={(newValues) =>
          handleChange("targetIndustries", newValues, setIndustryOptions)
        }
        onBlur={() => handleBlur("targetIndustries", formData.targetIndustries)}
        error={errors.targetIndustries} // ✅ and here
      />
    </div>
  );
};

export default MarketLandscape;
